import { localStorageKeys } from '@/core/configs/local-storage-keys'
import { BehaviorSubject, filter, Subscription, switchMap, take, tap } from 'rxjs'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable'
import { NumberLocalStorageValue } from '@/common/local-storage/NumberLocalStorageValue'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService'
import type { ObjectTypeModel } from '@/core/gql/gql-models-override.ts'

export class NewItemButtonController {
  public get lastSelectedType$(): IBehaviorObservable<ObjectTypeModel | undefined> {
    return this._lastSelectedType
  }

  public set lastSelectedType(type: ObjectTypeModel) {
    this.typesSubscription?.unsubscribe()
    this._lastSelectedType.next(type)
    this.lastSelectedTypeLsValue.value = type.id
  }

  private typesSubscription: Subscription
  private readonly _lastSelectedType: BehaviorSubject<ObjectTypeModel | undefined>
  private readonly lastSelectedTypeLsValue = new NumberLocalStorageValue(
    localStorageKeys.NewItemButton_LastSelectedType,
  )

  constructor(private readonly typesService: ObjectTypesService) {
    this.lastSelectedTypeLsValue.read()
    this._lastSelectedType = new BehaviorSubject<ObjectTypeModel | undefined>(
      this.computeInitialType(),
    )
  }

  public dispose(): void {
    this.typesSubscription?.unsubscribe()
  }

  public init(): void {
    this.typesSubscription = this.typesService.selectedSpaceTypes$
      .pipe(
        tap((types) => {
          this._lastSelectedType.next(types ? this.getTypeForLsValueOrDefault(types) : undefined)
        }),
      )
      .subscribe()
  }

  private computeInitialType(): ObjectTypeModel | undefined {
    const types = this.typesService.selectedSpaceTypes$.value
    if (!types) {
      return undefined
    }
    return this.getTypeForLsValueOrDefault(types)
  }

  // private onNonEmptyTypesFetched(types: ObjectTypeModel[]) {
  // }

  private getTypeForLsValueOrDefault(types: ObjectTypeModel[]): ObjectTypeModel | undefined {
    const typeForLsValue = this.tryGetTypeForLsValue()
    if (typeForLsValue) {
      return typeForLsValue
    }

    if (types.length > 0) {
      return types[0]
    }
    return undefined
  }

  private tryGetTypeForLsValue(): ObjectTypeModel | undefined {
    if (!this.lastSelectedTypeLsValue.value) {
      return undefined
    }
    const type = this.typesService.getTypeFromSelectedSpace(this.lastSelectedTypeLsValue.value)
    if (!type) {
      this.lastSelectedTypeLsValue.value = undefined
    }
    return type
  }
}
